import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { hasAccessToMultipleCountries } from "shared/imports/regionConstants"
import { isRegionDisabled, isRegionEnabled } from "shared/region-selection/helperFunctions"

import {
    MAJOR_REGION_SET,
    MAJOR_REGION_DESCRIPTIONS,
    PRODUCT_PAGE_PATHS,
    REGION_ABBREV_VALUE_MAPPING,
} from "@/components/TopNavigation/components/RegionSelector/constants"

// helpers based on app/static/frontend/subfeatures/RegionSelectionPage/selectors.js

const { SupportedRegion } = DjangIO.app.models

const getRegionDescription = (region) =>
    isRegionDisabled(region) ? "Not permissioned for your organization." : MAJOR_REGION_DESCRIPTIONS[region.value]

// top level groups: Global, Entire US, US Federal, US States & Local, US State, US Local, EU, EU Member States
const getMajorRegions = () =>
    [
        // using label and order field to overwrite the SupportedRegion fields while this is still feature flagged
        // order (which will probably change): Global, Entire US, Federal, States and Local, States, Local, EU, EU Members
        ...(isFeatureEnabled("ff_global_region") && hasAccessToMultipleCountries()
            ? [
                  {
                      ...SupportedRegion.user_global,
                      region_name: "Global",
                      order: 2,
                  },
              ]
            : []),
        {
            ...SupportedRegion.all_regions,
            region_name: "Entire US",
            order: 3,
        },
        {
            ...SupportedRegion.federal,
            region_name: "US Federal",
            order: 4,
        },
        {
            ...SupportedRegion.states,
            region_name: "US States",
            order: 5,
        },
        {
            ...SupportedRegion.local,
            region_name: "US Local",
            order: 6,
        },
        {
            ...SupportedRegion.states_and_local,
            region_name: "US States & Local",
            order: 7,
        },
        {
            ...SupportedRegion.eu,
            order: 8,
        },
        {
            ...SupportedRegion.international,
            region_name: "International",
            order: 9,
        },
        {
            ...SupportedRegion.eu_member_states,
            order: 10,
        },
    ]
        // add the tooltip text, and url to corresponding product page
        .map((region) => ({
            ...region,
            description: getRegionDescription(region),
            productPagePath: PRODUCT_PAGE_PATHS[region.value],
        }))
        .sort((a, b) => a.order - b.order)

export const sortAlphabetically = (reg1, reg2) =>
    reg1.region_name.toLowerCase() < reg2.region_name.toLowerCase() ? -1 : 1

export const formatRegion = (reg) => ({
    ...reg,
    label: reg.region_name,
    disabled: isRegionDisabled(reg),
    isMajor: MAJOR_REGION_SET.has(reg.value),
})

export const getRegionGroups = () => {
    const majorRegions = getMajorRegions()

    const regionGroups = [
        // most common top-level groups
        {
            groupLabel: "Major",
            key: "major-regions",
            regions: majorRegions.map(formatRegion),
        },
        // international
        {
            groupLabel: SupportedRegion.international.group_label,
            key: SupportedRegion.international.key,
            regions: SupportedRegion.international.child_regions
                .map((region) => SupportedRegion.by_value(region))
                // Until all international regions are ready, we want to hide unpermissioned international regions
                // We currently can't answer questions in the sales process about when it'll be ready
                .filter((region) => isRegionEnabled(region))
                .sort(sortAlphabetically)
                .map(formatRegion),
        },
        // stateRegions
        {
            groupLabel: SupportedRegion.states.group_label,
            key: SupportedRegion.states.key,
            regions: SupportedRegion.states.child_regions
                .map((region) => SupportedRegion.by_value(region))
                .sort(sortAlphabetically)
                .map(formatRegion),
        },
        // localRegions
        {
            groupLabel: SupportedRegion.local.group_label,
            key: SupportedRegion.local.key,
            regions: SupportedRegion.local.child_regions
                .map(
                    (region) =>
                        SupportedRegion.by_value(region) as ReturnType<typeof SupportedRegion.by_value> & {
                            show_in_selector?: boolean
                        },
                )
                .filter((region) => region.show_in_selector)
                .sort(sortAlphabetically)
                .map(formatRegion),
        },
        // south america
        // asia pacific
        // antarctica
    ]

    return regionGroups
}

export const getCurrentRegionValue = () => {
    let currentRegionValue = String(REGION_ABBREV_VALUE_MAPPING[Userdata.current_regions[0]])

    //The value of the custom region has a prefix "custom" followed by the name.
    //in order to use the correct value on the RegionSelector.tsx, we are adding the prefix here
    if (Userdata.current_preset_name) {
        currentRegionValue = `custom-${Userdata.current_preset_name}`
    }

    return currentRegionValue
}
